import type { AuthContextType } from '@/context/auth-context';
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  useLocation,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { Toaster } from '@/components/ui/sonner';
import type { QueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Link } from '@tanstack/react-router';
import { LayoutWrapper } from '@/components/layout/layout-wrapper';
import { useTrackScreenView } from '@/features/tracking/useTrackScreenView';
import { WebsiteChatbotGate } from '@/features/website-chatbot';
import { lazy, Suspense, type ReactNode } from 'react';

interface MyRouterContext {
  auth: AuthContextType;
  queryClient: QueryClient;
}

// Routes that need Google Maps / Places.
// The APIProvider is only mounted when the current pathname starts with one of these prefixes
// so that /home and other marketing pages do not download the Maps SDK.
const MAPS_PREFIXES = ['/booking', '/dashboard', '/apply-cleaner', '/cleaner'];

const LazyAPIProvider = lazy(() =>
  import('@vis.gl/react-google-maps').then(m => ({ default: m.APIProvider }))
);

function GoogleMapsGate({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const needsMaps = MAPS_PREFIXES.some(prefix => pathname.startsWith(prefix));

  if (!needsMaps) {
    return <>{children}</>;
  }

  return (
    <Suspense fallback={<>{children}</>}>
      <LazyAPIProvider
        apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY!}
        libraries={['places']}
      >
        {children}
      </LazyAPIProvider>
    </Suspense>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          404
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
          Page not found
        </p>
        <Button asChild>
          <Link to="/">Go Home</Link>
        </Button>
      </div>
    </div>
  );
}

function RootComponent() {
  useTrackScreenView();

  return (
    <GoogleMapsGate>
      <HeadContent />
      <LayoutWrapper>
        <Outlet />
      </LayoutWrapper>
      <Toaster />
      <WebsiteChatbotGate />

      {process.env.NODE_ENV === 'development' && <TanStackRouterDevtools />}
    </GoogleMapsGate>
  );
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFound,
});
